import type { FC } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { LoaderCircle, MessagePopup, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getLocaleTimezoneIsoDate, MAX_DECIMAL_12_4, MIN_PRICE } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FormikProps } from 'formik'
import { Formik, Form as FormikForm } from 'formik'
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon'
import UndoIcon from 'mdi-react/UndoIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LoadingError } from '@/components/LoadingError'
import Tabs from '@/components/Tabs'
import { UnsavedChangesButton } from '@/components/UnsavedChangesButton'
import {
  ADMIN_ROLES,
  USER_ROLE,
  useHasAssetEditingAccess,
  useHasRoleBasedAccess,
} from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import type { UpdateAssetPayload } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { getActiveBuyoutOfferByAsset } from '@/modules/admin/resources/buyout-offers'
import type { Policy } from '@/resources/ecom-policy'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { leasingResources } from '@/resources/leasing'
import { commonQueries } from '@/resources/queries'
import { isEndDateLater } from '@/util/formatters/time'
import { getPolicyFilter } from '@/util/getPolicyFilter'

import { AssetOrigin } from '../AssetOrigin'
import { EndOfLease } from '../EndOfLease'
import { mapPayloadWithLockedFields, getCostObjectById, getInitialValues } from '../helpers'
import type { FormFields } from '../types'

import { ActiveBuyoutOfferBanner } from './ActiveBuyoutOfferBanner'
import { Asset } from './Asset'
import { LeasingInfo } from './LeasingInfo'
import { UserInfo } from './UserInfo'

export const Form: FC<{
  assetId: number
  onChangeUserTypeId: (userTypeId: number) => void
}> = ({ assetId, onChangeUserTypeId }) => {
  const hasEditAccess = useHasAssetEditingAccess()

  const formRef = useRef<FormikProps<FormFields> | null>(null)
  const hasLocationsAccess = useHasRoleBasedAccess([
    ...ADMIN_ROLES,
    USER_ROLE.IT_USER,
    USER_ROLE.IT_PURCHASER,
  ])

  const [isHideOpen, setIsHideOpen] = useState(false)
  const [isUnHideOpen, setIsUnHideOpen] = useState(false)

  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const translate = useTranslation()

  const [saveError, setSaveError] = useState('')
  const saveAssetMutation = useMutation(adminResources.updateAsset, {
    onError(error: { text: string; response: Response; json: Object }) {
      setSaveError(error.response.statusText)
    },
    onSettled() {
      queryClient.invalidateQueries(queries.adminAsset(assetId))
      queryClient.invalidateQueries(queries.adminAssetList({ allAssets: true }), {
        refetchType: 'all',
      })
    },
  })
  const { data: customers, isError: isCustomerError } = useQuery(
    queries.adminCustomers(),
    adminResources.getCustomers
  )
  const { data: employees, isError: isEmployeesError } = useQuery(
    queries.adminEmployeeList(hasLocationsAccess),
    () => adminResources.getEmployees(hasLocationsAccess)
  )
  const { data: assetDetails, isError: isAssetDetailsError } = useQuery(
    queries.adminAsset(assetId),
    () => adminResources.getAsset(assetId)
  )

  const { data: activeBuyoutOffer, isLoading: isActiveBuyoutOfferLoading } = useQuery(
    queries.adminActiveBuyoutOfferByAsset(assetId),
    () => getActiveBuyoutOfferByAsset(assetId),
    { enabled: Boolean(assetId) }
  )

  const { data: availablePolicies } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )

  const { data: leasingAssetDetails, isError: isLeasingAssetDetailsError } = useQuery(
    queries.adminLeasingAssetDetails(assetId),
    () => leasingResources.getLeasingAssetDetails(assetId),
    { enabled: Boolean(assetDetails) && assetDetails?.isLeased }
  )

  const activePolicies = getPolicyFilter<Policy>({
    policyList: availablePolicies,
    countryId: assetDetails?.countryId,
    customerHasLeasing: assetDetails?.customerHasLeasing || false,
    policyId: Number(assetDetails?.policyId),
  })

  const isEcomPoliciesAvailable = activePolicies.length > 0

  const deleteAsset = async (id: number) => {
    if (formRef.current?.values) {
      await onSaveAsset(formRef.current?.values)
    }
    await adminResources.deleteAsset(id)
  }

  const activateAsset = async (id: number) => {
    if (formRef.current?.values) {
      await onSaveAsset(formRef.current?.values)
    }
    await adminResources.activateAsset(id)
  }

  const onSaveAsset = async (formSubmittedValues: FormFields) => {
    const { ecomPolicyId, ...formValues } = formSubmittedValues
    if (!formRef.current?.dirty || !assetDetails || activeBuyoutOffer?.id) return

    if (!formValues.customerId) throw Error('Customer id missing')

    if (isEcomPoliciesAvailable && !ecomPolicyId)
      throw Error('Ecom policies are available but none was selected')

    const lockedFields = assetDetails.isLeased ? leasingAssetDetails?.leasingFieldLocks : undefined
    const newLockedFieldsList = assetDetails.isLeased ? leasingAssetDetails?.lockFields : undefined

    // We display username and phone number but send only the username
    let costObject
    if (!(lockedFields?.assetUser || newLockedFieldsList?.includes('assetUser')) && employees) {
      costObject = getCostObjectById(employees, formValues.costObjectId)
    }

    const updateAssetPayload: UpdateAssetPayload = {
      ...formValues,
      userName: costObject?.name ?? '',
      ecomPolicyId: Number(ecomPolicyId),
      cost: Number(formValues.cost),
      returnStatusId: assetDetails.returnStatusId,
      result: assetDetails.result,
      resultCost: assetDetails.resultCost,
      deleteCertificate: assetDetails.deleteCertificate,
      deleteCertificateDate: assetDetails.deleteCertificateDate
        ? getLocaleTimezoneIsoDate(assetDetails.deleteCertificateDate)
        : '',
    }
    const payload = mapPayloadWithLockedFields({
      formValues: updateAssetPayload,
      assetStatusId: assetDetails.statusId,
      locked: {
        leasingFieldLocks: lockedFields,
        lockedFields: newLockedFieldsList,
      },
    })
    saveAssetMutation.mutate({ id: assetId, payload })
  }

  const { mutate: mutateHide, isLoading: isHideLoading } = useMutation(deleteAsset, {
    onSuccess: () => {
      queryClient.invalidateQueries(queries.adminAssetList({ allAssets: true }), {
        refetchType: 'all',
      })
      queryClient.invalidateQueries(queries.adminAsset(assetId))
      navigate('/hardware')
    },
  })

  const { mutate: mutateUnHide, isLoading: isUnHideLoading } = useMutation(activateAsset, {
    onSuccess: () => {
      queryClient.invalidateQueries(queries.adminAssetList({ allAssets: true }), {
        refetchType: 'all',
      })
      queryClient.invalidateQueries(queries.adminAsset(assetId))
      navigate('/hardware')
    },
  })

  const assetDetailsTabs = useMemo(() => {
    const tabs = [
      {
        value: 'user-info',
        label: (
          <div id="inactive-tab-asset">
            {isEcomPoliciesAvailable && !formRef.current?.values.ecomPolicyId && (
              <Tooltip
                data-tooltip-id="inactive-tab-asset"
                content={translate('Ecom policy is mandatory')}
              />
            )}
            <Translation className="capitalize" id="User info" />
          </div>
        ),
      },

      {
        value: 'asset',
        label: <Translation className="capitalize" id="Asset" />,
      },
      {
        value: 'asset-origin',
        label: (
          <div id="inactive-tab-origin">
            {isEcomPoliciesAvailable && !formRef.current?.values.ecomPolicyId && (
              <Tooltip
                anchorId="inactive-tab-origin"
                content={translate('Ecom policy is mandatory')}
              />
            )}
            <Translation className="capitalize" id="Origin" />
          </div>
        ),
      },
    ]

    if (assetDetails?.isLeased) {
      tabs.splice(2, 0, {
        value: 'leasing-info',
        label: <Translation className="capitalize" id="Leasing info" />,
      })
    }

    if (
      assetDetails?.isLeased &&
      (leasingAssetDetails?.currentEndOfLeaseWorkflow ||
        leasingAssetDetails?.endOfLeaseWorkflowHistory?.length)
    ) {
      tabs.splice(3, 0, {
        value: 'end-of-lease',
        label: <Translation className="capitalize" id="End of lease" />,
      })
    }
    return tabs
  }, [
    assetDetails?.isLeased,
    leasingAssetDetails?.currentEndOfLeaseWorkflow,
    leasingAssetDetails?.endOfLeaseWorkflowHistory,
    isEcomPoliciesAvailable,
    translate,
  ])

  const [queryTab, setQueryTab] = useSearchParamsByKey('tab')
  const [tabFilter, setTabFilter] = useState(queryTab ?? assetDetailsTabs[0].value)

  useEffect(() => {
    setTabFilter(queryTab ?? assetDetailsTabs[0].value)
  }, [assetDetailsTabs, queryTab, tabFilter])

  if (
    isEmployeesError ||
    isCustomerError ||
    isAssetDetailsError ||
    (assetDetails && assetDetails.isLeased && isLeasingAssetDetailsError)
  ) {
    return <LoadingError />
  }

  if (!assetDetails || !employees || !customers || isActiveBuyoutOfferLoading) {
    return <LoaderCircle />
  }

  const hasNoCost = assetDetails.cost === 0 || assetDetails.cost === null

  const lockedFields = assetDetails.isLeased ? leasingAssetDetails?.leasingFieldLocks : undefined
  let newLockedFieldsList = assetDetails.isLeased ? leasingAssetDetails?.lockFields : undefined

  return (
    <>
      <Formik<FormFields>
        enableReinitialize
        innerRef={formRef}
        validateOnBlur={false}
        initialValues={getInitialValues(assetDetails, employees)}
        validate={values => {
          const errors: Partial<Record<keyof FormFields, string>> = {}

          if (
            (!lockedFields?.assetCustomer || !newLockedFieldsList?.includes('customerId')) &&
            !values.customerId
          ) {
            errors.customerId = translate('This field is mandatory')
          } else if (
            (!lockedFields?.assetCustomer || !newLockedFieldsList?.includes('customerId')) &&
            values.customerId &&
            !customers?.find(customer => customer.id === values.customerId)
          ) {
            errors.customerId = translate('Please choose one of the available companies')
          }
          // We should show only employees that belong to the selected customer
          if (
            (!lockedFields?.assetUser || !newLockedFieldsList?.includes('assetUser')) &&
            values.costObjectId &&
            values.customerId &&
            getCostObjectById(employees, values.costObjectId)?.customerId !== values.customerId
          ) {
            errors.costObjectId = translate('Please select one of the available employees')
          }
          if (hasNoCost && !values.cost) {
            errors.cost = translate('This field is mandatory')
          }
          if (values.cost && Number(values.cost) > MAX_DECIMAL_12_4) {
            errors.cost = translate('The cost is too big')
          }
          if (values.cost && Number(values.cost) < MIN_PRICE) {
            errors.cost = translate('The cost is too small')
          }
          if (
            (!lockedFields?.assetDescription ||
              !newLockedFieldsList?.includes('assetDescription')) &&
            !values.assetDescription
          ) {
            errors.assetDescription = translate('This field is mandatory')
          }
          if (isEcomPoliciesAvailable && !values.ecomPolicyId) {
            errors.ecomPolicyId = translate('This field is mandatory')
          }

          if (
            (!lockedFields?.leaseStart || !newLockedFieldsList?.includes('leaseStart')) &&
            (lockedFields?.leaseEnd || newLockedFieldsList?.includes('leaseEnd')) &&
            values.leaseStart &&
            values.leaseEnd &&
            !isEndDateLater(values.leaseStart, values.leaseEnd)
          ) {
            errors.leaseStart = translate(
              'Please ensure the start date is earlier than the end date.'
            )
          }

          const leaseDateLocked =
            (lockedFields?.leaseStart || newLockedFieldsList?.includes('leaseStart')) &&
            (lockedFields?.leaseEnd || newLockedFieldsList?.includes('leaseEnd'))

          if (
            !leaseDateLocked &&
            values.leaseStart &&
            values.leaseEnd &&
            !isEndDateLater(values.leaseStart, values.leaseEnd)
          ) {
            errors.leaseEnd = translate('Please ensure the end date is later than the start date.')
          }

          return errors
        }}
        initialTouched={{
          customerId: true,
          costObjectId: true,
          assetDescription: true,
          ecomPolicyId: true,
        }}
        validateOnMount
        onSubmit={(formValues: FormFields) => onSaveAsset(formValues)}
      >
        {({ dirty, isValid }) => (
          <FormikForm>
            <Tabs
              selectedValue={tabFilter}
              tabs={assetDetailsTabs}
              onClick={tabId => {
                setQueryTab(String(tabId))
              }}
            />

            <ActiveBuyoutOfferBanner assetDetails={assetDetails} />

            {tabFilter === 'user-info' && (
              <UserInfo
                assetDetails={assetDetails}
                onChangeUserTypeId={onChangeUserTypeId}
                isFormDisabled={Boolean(activeBuyoutOffer?.id) || !hasEditAccess}
              />
            )}

            {tabFilter === 'asset' && (
              <Asset
                activePolicies={activePolicies}
                assetDetails={assetDetails}
                onChangeUserTypeId={onChangeUserTypeId}
                isFormDisabled={Boolean(activeBuyoutOffer?.id) || !hasEditAccess}
                onSetFormValues={(updatedValues: Partial<FormFields>) =>
                  formRef.current?.setValues({ ...formRef.current.values, ...updatedValues })
                }
              />
            )}

            {tabFilter === 'leasing-info' && <LeasingInfo assetDetails={assetDetails} />}

            {tabFilter === 'end-of-lease' && <EndOfLease />}

            {tabFilter === 'asset-origin' && <AssetOrigin />}

            <footer className="mt-6 flex flex-wrap justify-center sm:justify-end">
              {hasEditAccess &&
                (assetDetails.active ? (
                  <Button
                    variant="danger"
                    onClick={() => setIsHideOpen(true)}
                    Icon={<DeleteOutlineIcon />}
                    isLoading={isHideLoading || saveAssetMutation.isLoading}
                  >
                    <Translation id="Delete asset" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => setIsUnHideOpen(true)}
                    Icon={<UndoIcon />}
                    isLoading={isUnHideLoading || saveAssetMutation.isLoading}
                  >
                    <Translation id="Unhide asset" />
                  </Button>
                ))}

              {tabFilter !== 'asset-origin' && hasEditAccess && (
                <UnsavedChangesButton
                  error={!isValid}
                  hasUnsavedChanges={dirty}
                  isLoading={saveAssetMutation.isLoading}
                  disabled={Boolean(activeBuyoutOffer?.id)}
                />
              )}
            </footer>
          </FormikForm>
        )}
      </Formik>

      {/* Messagebox to show warning for hiding the asset */}
      <MessagePopup
        title={<Translation id="Are you absolutely sure?" />}
        isOpen={isHideOpen}
        onClose={() => setIsHideOpen(false)}
        message={
          <div>
            <p>
              <Translation id="Hide asset" />: {assetDetails.assetDescription} ({assetDetails.id}).
            </p>

            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setIsHideOpen(false)}>
                <Translation id="Cancel" />
              </Button>

              <Button
                variant="primary"
                onClick={() => mutateHide(assetId)}
                isLoading={isHideLoading}
              >
                <Translation id="Continue" />
              </Button>
            </div>
          </div>
        }
      />

      {/* Messagebox to show warning for un-hiding the asset */}
      <MessagePopup
        title={<Translation id="Are you absolutely sure?" />}
        isOpen={isUnHideOpen}
        onClose={() => setIsUnHideOpen(false)}
        message={
          <div>
            <p>
              <Translation id="Unhide asset" />: {assetDetails.assetDescription} ({assetDetails.id}
              ).
            </p>

            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setIsUnHideOpen(false)}>
                <Translation id="Cancel" />
              </Button>

              <Button
                variant="primary"
                onClick={() => mutateUnHide(assetId)}
                isLoading={isUnHideLoading}
              >
                <Translation id="Continue" />
              </Button>
            </div>
          </div>
        }
      />

      <MessagePopup
        title={<Translation id="Saving form error" />}
        message={
          <>
            <Translation id="We encountered a problem while saving your changes. Please refresh and try again." />{' '}
            <Translation id="Error" />: {saveError}
          </>
        }
        isOpen={Boolean(saveError)}
        onClose={() => setSaveError('')}
      />
    </>
  )
}
